import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Video, VideoOff, Mic, MicOff, PhoneOff, User, Music, CheckCircle, XCircle, RefreshCw, Volume2, Settings, Radio, Monitor } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Audio Level Meter Component
const AudioMeter = ({ stream, label }) => {
  const [level, setLevel] = useState(0);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  
  useEffect(() => {
    if (!stream) return;
    
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setLevel(Math.min(100, avg * 1.5));
        animationRef.current = requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
      
      return () => {
        cancelAnimationFrame(animationRef.current);
        audioContext.close();
      };
    } catch (e) {
      console.log('Audio meter error:', e);
    }
  }, [stream]);
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-400">{label}</span>
      <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-75 ${level > 70 ? 'bg-red-500' : level > 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
          style={{ width: `${level}%` }}
        />
      </div>
    </div>
  );
};

const JoinSessionNew = () => {
  const { roomCode: urlRoomCode } = useParams();
  
  // Camera states
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [stream, setStream] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  
  // Audio control states
  const [remoteVolume, setRemoteVolume] = useState(100);
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [pushToTalk, setPushToTalk] = useState(false);
  const [isPTTActive, setIsPTTActive] = useState(false);
  
  // Join states
  const [roomCode] = useState(urlRoomCode || '');
  const [name, setName] = useState('');
  const [step, setStep] = useState('camera'); // camera, session
  const [clientId, setClientId] = useState(null);
  const [hostPresent, setHostPresent] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  
  // THE CRITICAL FIX: Single video ref that NEVER changes
  // This video element is ALWAYS in the DOM, we just move it visually
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const streamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastPollTimeRef = useRef('');
  const hostNotifiedRef = useRef(false);
  const processedOffersRef = useRef(new Set());
  
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  // Attach stream to local video element - runs whenever stream changes
  useEffect(() => {
    if (stream && localVideoRef.current) {
      const video = localVideoRef.current;
      console.log('[Client] Attaching stream to video element');
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.muted = true;
      
      const playVideo = async () => {
        try {
          await video.play();
          setVideoPlaying(true);
          console.log('[Client] Local video now playing');
        } catch (err) {
          console.log('[Client] Auto-play failed, user tap required:', err.message);
        }
      };
      
      playVideo();
      video.onloadedmetadata = () => {
        console.log('[Client] Video metadata loaded');
        playVideo();
      };
    }
  }, [stream]);

  // Attach remote stream to video element
  useEffect(() => {
    if (remoteStream && remoteVideoRef.current) {
      console.log('[Client] Attaching remote stream with', remoteStream.getTracks().length, 'tracks');
      remoteStream.getTracks().forEach(t => console.log('[Client] Remote track:', t.kind, t.enabled, t.readyState));
      remoteVideoRef.current.srcObject = remoteStream;
      // Ensure NOT muted so we hear the host
      remoteVideoRef.current.muted = false;
      remoteVideoRef.current.volume = remoteVolume / 100;
      remoteVideoRef.current.play().catch(e => console.log('[Client] Remote play error:', e));
    }
  }, [remoteStream, remoteVolume]);

  // Update remote volume when slider changes
  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.volume = remoteVolume / 100;
    }
  }, [remoteVolume]);

  // Enumerate audio devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        setAudioDevices(audioInputs);
        if (audioInputs.length > 0 && !selectedAudioDevice) {
          setSelectedAudioDevice(audioInputs[0].deviceId);
        }
      } catch (e) {
        console.log('Error enumerating devices:', e);
      }
    };
    getDevices();
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => navigator.mediaDevices.removeEventListener('devicechange', getDevices);
  }, [selectedAudioDevice]);

  // Push-to-Talk keyboard handler
  useEffect(() => {
    if (!pushToTalk || step !== 'session') return;
    
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsPTTActive(true);
        if (streamRef.current) {
          const audioTrack = streamRef.current.getAudioTracks()[0];
          if (audioTrack) audioTrack.enabled = true;
        }
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsPTTActive(false);
        if (streamRef.current) {
          const audioTrack = streamRef.current.getAudioTracks()[0];
          if (audioTrack) audioTrack.enabled = false;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pushToTalk, step]);

  // When PTT mode changes, update audio track
  useEffect(() => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        // If PTT is enabled, mute by default (unmute on spacebar)
        // If PTT is disabled, respect audioEnabled state
        audioTrack.enabled = pushToTalk ? isPTTActive : audioEnabled;
      }
    }
  }, [pushToTalk, isPTTActive, audioEnabled]);

  // Enable camera with enhanced audio
  const enableCamera = async (audioDeviceId = null) => {
    setStatus('requesting');
    setErrorMsg('');
    setVideoPlaying(false);
    
    try {
      const deviceId = audioDeviceId || selectedAudioDevice;
      console.log('[Client] Requesting camera + audio access...', deviceId ? `device: ${deviceId}` : 'default');
      
      // Build audio constraints - only add deviceId if we have a valid one
      const audioConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      
      // Only add deviceId if it's a valid non-empty string
      if (deviceId && deviceId.length > 0) {
        audioConstraints.deviceId = { ideal: deviceId };
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: audioConstraints
      });
      console.log('[Client] Got stream:', mediaStream.getTracks().map(t => `${t.kind}:${t.readyState}`));
      
      // MUTE MIC BY DEFAULT to prevent feedback
      mediaStream.getAudioTracks().forEach(track => {
        track.enabled = false;
      });
      setAudioEnabled(false);
      
      setStream(mediaStream);
      streamRef.current = mediaStream;
      setStatus('success');
      
      toast.info('Mic muted by default - click mic button to unmute');
    } catch (err) {
      console.error('[Client] Camera error:', err);
      setStatus('error');
      if (err.name === 'NotAllowedError') {
        setErrorMsg('Camera permission denied. Check browser settings.');
      } else if (err.name === 'NotFoundError') {
        setErrorMsg('No camera found on this device.');
      } else if (err.name === 'OverconstrainedError') {
        setErrorMsg('Camera/mic settings not supported. Try again.');
      } else {
        setErrorMsg(err.message);
      }
    }
  };

  // Switch audio device
  const switchAudioDevice = async (deviceId) => {
    setSelectedAudioDevice(deviceId);
    if (stream) {
      // Stop old audio tracks
      stream.getAudioTracks().forEach(t => t.stop());
      
      try {
        // Get new audio with selected device
        const newAudioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { ideal: deviceId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        
        const newAudioTrack = newAudioStream.getAudioTracks()[0];
        
        // Replace in local stream
        const oldAudioTrack = stream.getAudioTracks()[0];
        if (oldAudioTrack) {
          stream.removeTrack(oldAudioTrack);
        }
        stream.addTrack(newAudioTrack);
        streamRef.current = stream;
        
        // Replace in peer connection if exists
        if (peerConnectionRef.current) {
          const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'audio');
          if (sender) {
            await sender.replaceTrack(newAudioTrack);
          }
        }
        
        toast.success('Audio device switched');
      } catch (e) {
        console.error('Error switching audio device:', e);
        toast.error('Failed to switch audio device');
      }
    }
  };

  // Force play on tap (iOS Safari needs user gesture)
  const forcePlay = async () => {
    if (localVideoRef.current && streamRef.current) {
      const video = localVideoRef.current;
      video.srcObject = streamRef.current;
      try {
        await video.play();
        setVideoPlaying(true);
        console.log('[Client] Force play succeeded');
      } catch (e) {
        console.log('[Client] Force play failed:', e);
      }
    }
  };

  // Join session - calls backend API
  const joinSession = async () => {
    if (!name.trim() || !streamRef.current) return;
    
    toast.info('Joining session...');
    
    try {
      console.log('[Client] Joining room:', roomCode);
      const res = await fetch(`${API}/api/remote-sessions/join/${roomCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'client', name: name })
      });
      
      const data = await res.json();
      console.log('[Client] Join response:', data);
      
      if (data.success) {
        setClientId(data.client_id);
        setHostPresent(data.host_present);
        setStep('session');
        
        if (data.host_present) {
          toast.success('Host is online!');
          hostNotifiedRef.current = true;
        } else {
          toast.info('Waiting for host...');
        }
        
        startPolling(data.client_id);
      } else {
        toast.error(data.error || 'Failed to join');
      }
    } catch (err) {
      console.error('[Client] Join error:', err);
      toast.error('Connection error: ' + err.message);
    }
  };

  // Polling for WebRTC signaling
  const startPolling = useCallback((cid) => {
    console.log('[Client] Starting polling for client:', cid);
    
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/remote-sessions/poll/${roomCode}?role=${cid}&since=${lastPollTimeRef.current}`);
        const data = await res.json();
        lastPollTimeRef.current = data.timestamp || '';
        
        // Host presence check
        if (data.host_present && !hostNotifiedRef.current) {
          hostNotifiedRef.current = true;
          setHostPresent(true);
          toast.success('Host is now online!');
        }
        
        // Handle offers from host
        if (data.offers?.length > 0) {
          for (const offer of data.offers) {
            const offerKey = `${offer.from_id}-${offer.timestamp}`;
            if (!processedOffersRef.current.has(offerKey) && !peerConnectionRef.current) {
              processedOffersRef.current.add(offerKey);
              console.log('[Client] Processing offer from host');
              await handleOffer(offer.from_id, offer.sdp, cid);
            }
          }
        }
        
        // Handle ICE candidates
        if (data.ice_candidates?.length > 0 && peerConnectionRef.current) {
          for (const ice of data.ice_candidates) {
            if (ice.candidate) {
              try {
                await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(ice.candidate));
              } catch (e) {
                console.log('[Client] ICE add error:', e);
              }
            }
          }
        }
      } catch (err) {
        console.error('[Client] Poll error:', err);
      }
    };
    
    poll();
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(poll, 1500);
  }, [roomCode]);

  // Handle WebRTC offer from host
  const handleOffer = async (hostId, sdp, currentClientId) => {
    console.log('[Client] Creating peer connection for offer');
    
    const pc = new RTCPeerConnection(rtcConfig);
    
    // Add local tracks from our camera stream
    const currentStream = streamRef.current;
    if (currentStream) {
      const tracks = currentStream.getTracks();
      console.log('[Client] Adding', tracks.length, 'local tracks to peer connection');
      tracks.forEach(track => {
        pc.addTrack(track, currentStream);
      });
      toast.info(`Sending ${tracks.length} tracks to host`);
    } else {
      console.warn('[Client] No local stream available!');
      toast.error('No camera stream - host will not see you');
    }
    
    // Handle remote tracks from host
    pc.ontrack = (event) => {
      console.log('[Client] Got remote track:', event.track.kind);
      // Check if this is our own stream (loopback prevention)
      const receivedStream = event.streams[0];
      if (streamRef.current && receivedStream && receivedStream.id === streamRef.current.id) {
        console.log('[Client] Ignoring loopback stream');
        return;
      }
      setRemoteStream(receivedStream);
      setIsConnected(true);
      toast.success('Connected to host!');
    };
    
    // Handle ICE candidates - send to host
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[Client] Sending ICE candidate');
        fetch(`${API}/api/remote-sessions/signal/${roomCode}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'ice-candidate',
            from_id: currentClientId,
            to_id: 'host',
            candidate: event.candidate
          })
        });
      }
    };
    
    pc.onconnectionstatechange = () => {
      console.log('[Client] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setIsConnected(true);
      }
    };
    
    peerConnectionRef.current = pc;
    
    try {
      // Set remote description (the offer)
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp }));
      console.log('[Client] Set remote description');
      
      // Create answer
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log('[Client] Created answer, waiting for ICE gathering...');
      
      // Wait for ICE gathering to complete (bundles candidates into SDP)
      await new Promise((resolve) => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          const check = () => {
            console.log('[Client] ICE gathering state:', pc.iceGatheringState);
            if (pc.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', check);
              resolve();
            }
          };
          pc.addEventListener('icegatheringstatechange', check);
          setTimeout(resolve, 5000);
        }
      });
      
      // Send answer with bundled ICE candidates
      console.log('[Client] Sending answer to host');
      await fetch(`${API}/api/remote-sessions/signal/${roomCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'answer',
          from_id: currentClientId,
          to_id: 'host',
          client_id: currentClientId,
          sdp: pc.localDescription.sdp
        })
      });
      
      console.log('[Client] Answer sent successfully');
    } catch (err) {
      console.error('[Client] Error handling offer:', err);
      toast.error('Connection error: ' + err.message);
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoEnabled(track.enabled);
      }
    }
  };

  // Toggle audio
  const toggleAudio = () => {
    if (streamRef.current) {
      const track = streamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setAudioEnabled(track.enabled);
      }
    }
  };

  // Toggle screen share
  const toggleScreenShare = async () => {
    if (screenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
      // Restore camera video to peer connection
      if (peerConnectionRef.current && streamRef.current) {
        const videoTrack = streamRef.current.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender && videoTrack) {
          sender.replaceTrack(videoTrack);
        }
      }
      setScreenSharing(false);
      toast.info('Screen share stopped');
    } else {
      // Start screen sharing
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always' },
          audio: true
        });
        screenStreamRef.current = screenStream;
        
        // Replace video track in peer connection with screen
        if (peerConnectionRef.current) {
          const screenTrack = screenStream.getVideoTracks()[0];
          const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
          if (sender && screenTrack) {
            sender.replaceTrack(screenTrack);
          }
          
          // Handle when user stops sharing via browser UI
          screenTrack.onended = () => {
            toggleScreenShare();
          };
        }
        
        setScreenSharing(true);
        toast.success('Screen sharing - Host can see your screen!');
      } catch (err) {
        console.error('Screen share error:', err);
        toast.error('Could not start screen sharing');
      }
    }
  };

  // Leave session
  const leaveSession = () => {
    console.log('[Client] Leaving session');
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (peerConnectionRef.current) peerConnectionRef.current.close();
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    
    // Reset state
    peerConnectionRef.current = null;
    processedOffersRef.current = new Set();
    hostNotifiedRef.current = false;
    
    setStep('camera');
    setStream(null);
    streamRef.current = null;
    setStatus('idle');
    setRemoteStream(null);
    setIsConnected(false);
    setHostPresent(false);
    setVideoPlaying(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (peerConnectionRef.current) peerConnectionRef.current.close();
    };
  }, []);

  // No room code - show error
  if (!roomCode) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center">
          <Music className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Join Session</h1>
          <p className="text-gray-400">Please use a link with a room code</p>
          <p className="text-gray-500 text-sm mt-2">Example: /join-session/CASA-XXX-XXXX</p>
        </div>
      </div>
    );
  }

  // =============================================
  // RENDER - THE KEY: Video element is ALWAYS rendered
  // We use absolute positioning to move it between views
  // =============================================
  
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      
      {/* ================================================
          THE SINGLE LOCAL VIDEO ELEMENT - NEVER UNMOUNTED
          Position changes based on step, but element stays
          ================================================ */}
      <div 
        className={`
          transition-all duration-300 ease-in-out overflow-hidden
          ${step === 'camera' 
            ? 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md h-64 rounded-xl z-10' 
            : 'absolute top-16 right-4 w-28 h-36 sm:w-32 sm:h-40 rounded-lg z-50 border-2 border-amber-500 shadow-lg'
          }
        `}
        style={{ backgroundColor: '#1a1a1a' }}
        onClick={forcePlay}
        data-testid="local-video-container"
      >
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }}
        />
        
        {/* Camera setup state indicators - only show in camera step */}
        {step === 'camera' && !stream && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            {status === 'requesting' ? (
              <div className="text-center">
                <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Requesting camera access...</p>
              </div>
            ) : status === 'error' ? (
              <div className="text-center text-red-400 p-4">
                <XCircle className="w-16 h-16 mx-auto mb-4" />
                <p className="font-bold text-xl mb-2">CAMERA FAILED</p>
                <p className="text-sm bg-red-900/50 p-3 rounded">{errorMsg}</p>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <VideoOff className="w-16 h-16 mx-auto mb-4" />
                <p>Enable camera to join</p>
              </div>
            )}
          </div>
        )}
        
        {/* Tap to play overlay - only when stream exists but not playing */}
        {stream && !videoPlaying && step === 'camera' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <div className="text-center">
              <Video className="w-12 h-12 mx-auto mb-2 text-amber-400" />
              <p className="text-amber-400 font-bold">TAP TO SEE VIDEO</p>
            </div>
          </div>
        )}
        
        {/* Camera ready badge - only in camera step */}
        {stream && step === 'camera' && (
          <div className="absolute bottom-2 left-2 bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            CAMERA READY
          </div>
        )}
        
        {/* Video disabled overlay - in session */}
        {step === 'session' && !videoEnabled && (
          <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
            <VideoOff className="w-8 h-8 text-gray-500" />
          </div>
        )}
        
        {/* Name badge - in session */}
        {step === 'session' && (
          <div className="absolute bottom-1 left-1 bg-amber-500 px-1.5 py-0.5 rounded text-xs text-black font-bold">
            {name || 'YOU'}
          </div>
        )}
      </div>

      {/* ================================================
          CAMERA SETUP VIEW
          ================================================ */}
      {step === 'camera' && (
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Music className="w-10 h-10 text-amber-400" />
            <h1 className="text-3xl font-bold">Escola de Samba Casa Samba</h1>
          </div>
          <p className="text-amber-400 mb-6 font-mono">Room: {roomCode}</p>

          {/* Spacer for video preview (video is absolutely positioned) */}
          <div className="w-full max-w-md h-64 mb-6"></div>

          {/* Name Input - Only show when camera is ready */}
          {stream && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full max-w-md bg-gray-800 border-2 border-gray-600 rounded-xl px-4 py-3 text-lg text-center mb-4 focus:border-amber-500 focus:outline-none"
              data-testid="name-input"
            />
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            {!stream ? (
              <button
                onClick={enableCamera}
                disabled={status === 'requesting'}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-black font-bold px-8 py-4 rounded-xl text-xl transition-colors"
                data-testid="enable-camera-btn"
              >
                ENABLE CAMERA
              </button>
            ) : (
              <button
                onClick={joinSession}
                disabled={!name.trim()}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-black font-bold px-8 py-4 rounded-xl text-xl transition-colors"
                data-testid="join-session-btn"
              >
                JOIN SESSION
              </button>
            )}
          </div>
        </div>
      )}

      {/* ================================================
          IN-SESSION VIEW
          ================================================ */}
      {step === 'session' && (
        <div className="min-h-screen flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="w-5 h-5 text-amber-400" />
              <span className="font-mono text-sm">{roomCode}</span>
            </div>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <span className="flex items-center gap-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" /> Connected
                </span>
              ) : hostPresent ? (
                <span className="flex items-center gap-1 text-amber-400 text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Connecting...
                </span>
              ) : (
                <span className="flex items-center gap-1 text-gray-400 text-sm">
                  Waiting for host...
                </span>
              )}
            </div>
          </div>
          
          {/* Remote Video Area */}
          <div className="flex-1 relative bg-gray-950">
            {/* Remote Video (Host) - Full Screen */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
              style={{ backgroundColor: '#000' }}
              onClick={() => {
                if (remoteVideoRef.current && remoteStream) {
                  remoteVideoRef.current.play().catch(() => {});
                }
              }}
            />
            
            {/* Placeholder when no remote stream */}
            {!remoteStream && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center text-gray-500">
                  <User className="w-20 h-20 mx-auto mb-4 opacity-30" />
                  <p className="text-xl">
                    {isConnected ? 'Connected!' : hostPresent ? 'Connecting to host...' : 'Waiting for host to start...'}
                  </p>
                  <p className="text-sm mt-2 text-gray-600">Room: {roomCode}</p>
                  {hostPresent && !isConnected && (
                    <div className="mt-4">
                      <RefreshCw className="w-6 h-6 mx-auto animate-spin text-amber-400" />
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Local video PiP is absolutely positioned at component level */}
          </div>
          
          {/* Controls */}
          <div className="bg-gray-900 p-4 pb-20">
            {/* Audio Level Meters */}
            <div className="flex justify-center gap-6 mb-3">
              <AudioMeter stream={streamRef.current} label="Your Mic" />
              <AudioMeter stream={remoteStream} label="Host Audio" />
            </div>
            
            {/* PTT Indicator */}
            {pushToTalk && (
              <div className={`text-center mb-2 py-1 rounded ${isPTTActive ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                {isPTTActive ? '🎤 TRANSMITTING...' : 'Hold SPACE to talk'}
              </div>
            )}
            
            <div className="flex items-center justify-center gap-3">
              {/* Mic Toggle / PTT Button */}
              {pushToTalk ? (
                <button
                  onMouseDown={() => {
                    setIsPTTActive(true);
                    if (streamRef.current) {
                      const track = streamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = true;
                    }
                  }}
                  onMouseUp={() => {
                    setIsPTTActive(false);
                    if (streamRef.current) {
                      const track = streamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = false;
                    }
                  }}
                  onMouseLeave={() => {
                    setIsPTTActive(false);
                    if (streamRef.current) {
                      const track = streamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = false;
                    }
                  }}
                  onTouchStart={() => {
                    setIsPTTActive(true);
                    if (streamRef.current) {
                      const track = streamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = true;
                    }
                  }}
                  onTouchEnd={() => {
                    setIsPTTActive(false);
                    if (streamRef.current) {
                      const track = streamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = false;
                    }
                  }}
                  className={`p-4 rounded-full transition-colors ${isPTTActive ? 'bg-green-500 scale-110' : 'bg-amber-600 hover:bg-amber-500'}`}
                  data-testid="ptt-btn"
                >
                  <Radio className="w-6 h-6" />
                </button>
              ) : (
                <button
                  onClick={toggleAudio}
                  className={`p-4 rounded-full transition-colors ${audioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500 hover:bg-red-600'}`}
                  data-testid="toggle-audio-btn"
                >
                  {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
                </button>
              )}
              
              <button
                onClick={toggleVideo}
                className={`p-4 rounded-full transition-colors ${videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500 hover:bg-red-600'}`}
                data-testid="toggle-video-btn"
              >
                {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
              </button>
              
              {/* Screen Share */}
              <button
                onClick={toggleScreenShare}
                className={`p-4 rounded-full transition-colors flex items-center gap-1 ${screenSharing ? 'bg-purple-500 hover:bg-purple-600 animate-pulse' : 'bg-gray-700 hover:bg-gray-600'}`}
                data-testid="screen-share-btn"
                title={screenSharing ? 'Stop sharing' : 'Share your screen'}
              >
                <Monitor className="w-6 h-6" />
                {screenSharing && <span className="text-xs ml-1">SHARING</span>}
              </button>
              
              {/* Audio Settings */}
              <button
                onClick={() => setShowAudioSettings(!showAudioSettings)}
                className={`p-3 rounded-full transition-colors flex items-center gap-1 ${showAudioSettings ? 'bg-blue-500' : 'bg-gray-700 hover:bg-gray-600'}`}
                data-testid="audio-settings-btn"
              >
                <Settings className="w-5 h-5" />
                <span className="text-xs">Settings</span>
              </button>
              
              <button
                onClick={leaveSession}
                className="p-4 bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                data-testid="leave-session-btn"
              >
                <PhoneOff className="w-6 h-6" />
              </button>
            </div>
            
            {/* Audio Settings Panel */}
            {showAudioSettings && (
              <div className="mt-4 p-4 bg-gray-800 rounded-xl mb-16">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Audio Settings
                </h3>
                
                {/* Volume Slider */}
                <div className="mb-4">
                  <label className="text-xs text-gray-400 mb-1 block">Host Volume: {remoteVolume}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={remoteVolume}
                    onChange={(e) => setRemoteVolume(parseInt(e.target.value))}
                    className="w-full accent-amber-500"
                  />
                </div>
                
                {/* Audio Device Selection */}
                <div className="mb-4">
                  <label className="text-xs text-gray-400 mb-1 block">Microphone</label>
                  <select
                    value={selectedAudioDevice}
                    onChange={(e) => switchAudioDevice(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm"
                  >
                    {audioDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Push-to-Talk Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm">Push-to-Talk Mode</span>
                  <button
                    onClick={() => setPushToTalk(!pushToTalk)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${pushToTalk ? 'bg-amber-500 text-black' : 'bg-gray-700'}`}
                  >
                    {pushToTalk ? 'ON' : 'OFF'}
                  </button>
                </div>
                {pushToTalk && (
                  <p className="text-xs text-gray-500 mt-2">
                    Hold SPACE key or tap the radio button to talk
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JoinSessionNew;
